import { useMemo } from "react";
import { Box, Button, Group, Stack, Text } from "@mantine/core";
import { IconSwitchHorizontal, IconTruckDelivery } from "@tabler/icons-react";
import {
  formatDateOnlyWithShortMonth,
  parseDateOnly,
} from "../../../utils/dateOnly";

interface Props {
  caseData: any;
  canAssignPathologist?: boolean;
  onAssignPathologist?: () => void;
  canReplacePathologist?: boolean;
  onReplacePathologist?: () => void;
}

const ACCESSIONING_TYPE_LABELS: Record<string, string> = {
  global: "Global",
  client: "Client",
  external: "External",
  additional_stain: "Additional Stain",
};

function formatAccessioningType(value: string | null | undefined): string {
  if (!value) return "—";
  return (
    ACCESSIONING_TYPE_LABELS[value] ||
    value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export default function CaseMetadataCardV2({
  caseData,
  canAssignPathologist = false,
  onAssignPathologist,
  canReplacePathologist = false,
  onReplacePathologist,
}: Props) {
  const pathologist = caseData?.pathologist;
  const createdBy = caseData?.createdBy;
  const procedure = caseData?.procedure;
  const salesRep = caseData?.salesRep;
  const receivedDate = useMemo(() => {
    const dateReceived = parseDateOnly(caseData?.dateReceived);
    return dateReceived ? formatDateOnlyWithShortMonth(dateReceived) : "—";
  }, [caseData?.dateReceived]);
  const collectedDate = useMemo(() => {
    const dateCollected = parseDateOnly(caseData?.dateCollected);
    return dateCollected ? formatDateOnlyWithShortMonth(dateCollected) : "—";
  }, [caseData?.dateCollected]);

  const initials = pathologist?.fullName
    ? pathologist.fullName
        .split(" ")
        .filter(Boolean)
        .map((w: string) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "—";

  return (
    <Box
      style={{
        background: "#f9fafb",
        borderRadius: 12,
        border: "1px solid var(--mantine-color-gray-2)",
        padding: 20,
      }}
    >
      <Text size="xs" fw={700} c="dimmed" tt="uppercase" lts="0.08em" mb="md">
        Case Metadata
      </Text>

      <Box mb="xs">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Received
          </Text>
          <Text size="sm" fw={500}>
            {receivedDate}
          </Text>
        </Group>
      </Box>

      <Box mb="xs">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Collected
          </Text>
          <Text size="sm" fw={500}>
            {collectedDate}
          </Text>
        </Group>
      </Box>

      <Box mb="xs">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Accessioned By
          </Text>
          <Text size="sm" fw={500}>
            {createdBy?.fullName || "—"}
          </Text>
        </Group>
      </Box>

      <Box mb="xs">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Procedure
          </Text>
          <Text size="sm" fw={500}>
            {procedure?.name || caseData?.procedureName || "—"}
          </Text>
        </Group>
      </Box>

      <Box mb="xs">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Case Type
          </Text>
          <Text size="sm" fw={500}>
            {formatAccessioningType(caseData?.accessioningType)}
          </Text>
        </Group>
      </Box>

      {caseData?.externalCaseNumber && (
        <Box mb="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              External Case #
            </Text>
            <Text size="sm" fw={500}>
              {caseData.externalCaseNumber}
            </Text>
          </Group>
        </Box>
      )}

      {caseData?.isThroughSalesRep && salesRep && (
        <Box mb="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Sales Rep
            </Text>
            <Text size="sm" fw={500}>
              {salesRep.name}
            </Text>
          </Group>
        </Box>
      )}

      <Box
        pt="md"
        mt="xs"
        style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
      >
        <Group justify="space-between" align="center" mb="sm">
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            Assigned Pathologist
          </Text>
          {canAssignPathologist && onAssignPathologist && (
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<IconSwitchHorizontal size={14} />}
              onClick={onAssignPathologist}
            >
              Assign pathologist
            </Button>
          )}
          {canReplacePathologist && onReplacePathologist && (
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<IconSwitchHorizontal size={14} />}
              onClick={onReplacePathologist}
            >
              Replace pathologist
            </Button>
          )}
        </Group>
        {pathologist ? (
          <Group gap="sm" wrap="nowrap">
            <Box
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                background: "#0b50da",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials}
            </Box>
            <Box>
              <Text size="sm" fw={700}>
                {pathologist.fullName}
              </Text>
              <Text size="xs" c="dimmed">
                {pathologist.role || "Senior Pathologist"}
                {pathologist.credentials ? `, ${pathologist.credentials}` : ""}
              </Text>
            </Box>
          </Group>
        ) : (
          <Text size="sm" c="dimmed" fs="italic">
            Not assigned
          </Text>
        )}
      </Box>

      {caseData?.accessioningType === "tc_only" && (
        <Box
          pt="md"
          mt="xs"
          style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
        >
          <Group gap={6} mb="sm">
            <IconTruckDelivery size={14} color="var(--mantine-color-gray-6)" />
            <Text size="xs" fw={700} c="dimmed" tt="uppercase">
              Shipping Destination
            </Text>
          </Group>
          <Stack gap="xs">
            <Box>
              <Text size="xs" c="dimmed">
                Shipping Pathologist
              </Text>
              <Text size="sm" fw={500}>
                {caseData.shippingPathologist || "—"}
              </Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">
                Shipping Address
              </Text>
              <Text size="sm" fw={500}>
                {[
                  caseData.shippingStreet,
                  caseData.shippingCity,
                  caseData.shippingState,
                  caseData.shippingZipCode,
                ]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </Text>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
